import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../../context/AuthContext';
import {
  listSchoolPeriods,
  createSchoolPeriod,
  updateSchoolPeriod,
  deleteSchoolPeriod,
} from '../../../api/schoolConfigApi';
import { showSuccess, showError } from '../../../utils/notifications';
import { getContextualError, logError } from '../../../utils/errorHandling';
import CollapsibleSection from '../../common/CollapsibleSection';
import { useLoadingStates } from '../../../hooks/useLoadingStates';
import adminStyles from '../AdminPanel.module.css';
import type { components } from '../../../api/types.gen';

type SchoolPeriodResponse = components['schemas']['SchoolPeriodResponse'];

interface SchoolPeriodsPanelProps {
  schoolId?: number;
  embedded?: boolean;
}

/** Minimal shape narrowed from a caught error to read its HTTP status. */
interface ApiErrorLike {
  status?: number;
}

/** Predicate flipped by the fetch effect's cleanup to drop a stale response. */
type IsCancelled = () => boolean;

// SchoolPeriod is the canonical, schedule-type-agnostic identity of a period
// at a school (e.g. "Period 4" always means the same academic period
// regardless of which ScheduleType is active that day) — see
// docs/SCHEMA_PLAN.md. This panel manages that canonical list directly;
// ScheduleVariantModal.js references it (rather than free-typing period
// names) when building each schedule type's bell times, and
// ClassSectionsPanel.js references it when assigning a class to a period.
//
// Mirrors DestinationsPanel.js's structure (fetch-on-mount + refetch-after-
// mutation, no real-time push).

const SchoolPeriodsPanel = ({
  schoolId: schoolIdProp,
  embedded = false,
}: SchoolPeriodsPanelProps = {}): React.ReactElement => {
  const { currentUser } = useAuth();
  const schoolId = schoolIdProp ?? currentUser?.schoolId;

  const [loadingStates, setLoading] = useLoadingStates({
    loadSchoolPeriods: false,
    addSchoolPeriod: false,
    updateSchoolPeriod: false,
    removeSchoolPeriod: false,
  });

  const [schoolPeriods, setSchoolPeriods] = useState<SchoolPeriodResponse[]>([]);

  // Guards against a stale response clobbering `schoolPeriods` for the wrong
  // schoolId: if schoolId changes while a fetch is in flight, the effect
  // below flips `cancelled` on cleanup so an earlier request that resolves
  // after a later one is dropped instead of overwriting state. Mirrors the
  // cancelled-flag pattern in useAdminSetupChecklist.js's fetchCounts.
  const fetchSchoolPeriods = useCallback(
    async (isCancelled: IsCancelled = () => false): Promise<void> => {
      if (!schoolId) return;

      if (!isCancelled()) setLoading('loadSchoolPeriods', true);
      try {
        const result = await listSchoolPeriods(schoolId);
        if (!isCancelled()) setSchoolPeriods(result.data);
      } catch (error) {
        logError(error, 'loadSchoolPeriods');
        if (!isCancelled()) {
          showError(
            getContextualError(
              error,
              undefined,
              'Failed to load periods. Please try again.'
            )
          );
          setSchoolPeriods([]);
        }
      } finally {
        if (!isCancelled()) setLoading('loadSchoolPeriods', false);
      }
    },
    [schoolId, setLoading]
  );

  useEffect(() => {
    let cancelled = false;
    fetchSchoolPeriods(() => cancelled);
    return () => {
      cancelled = true;
    };
  }, [fetchSchoolPeriods]);

  // Add form
  const [newName, setNewName] = useState('');
  const [newDisplayOrder, setNewDisplayOrder] = useState('');

  const addSchoolPeriod = async (): Promise<void> => {
    if (!newName.trim()) {
      showError('Please enter a period name.');
      return;
    }
    if (!newDisplayOrder.trim()) {
      showError('Please enter a display order.');
      return;
    }
    if (!schoolId) return;

    setLoading('addSchoolPeriod', true);
    try {
      await createSchoolPeriod(schoolId, {
        name: newName.trim(),
        displayOrder: Number(newDisplayOrder),
      });

      setNewName('');
      setNewDisplayOrder('');
      showSuccess(`Added ${newName.trim()} to periods list`);
      await fetchSchoolPeriods();
    } catch (error) {
      logError(error, 'addSchoolPeriod');
      if ((error as ApiErrorLike).status === 409) {
        showError(`A period named "${newName.trim()}" already exists.`);
      } else {
        showError(
          getContextualError(
            error,
            undefined,
            'Failed to add period. Please try again.'
          )
        );
      }
    } finally {
      setLoading('addSchoolPeriod', false);
    }
  };

  // Edit form
  const [editingSchoolPeriod, setEditingSchoolPeriod] = useState<number | null>(
    null
  );
  const [editName, setEditName] = useState('');
  const [editDisplayOrder, setEditDisplayOrder] = useState('');

  const startEditSchoolPeriod = (schoolPeriod: SchoolPeriodResponse): void => {
    setEditingSchoolPeriod(schoolPeriod.id);
    setEditName(schoolPeriod.name);
    setEditDisplayOrder(String(schoolPeriod.displayOrder));
  };

  const cancelEditSchoolPeriod = (): void => {
    setEditingSchoolPeriod(null);
    setEditName('');
    setEditDisplayOrder('');
  };

  const submitUpdateSchoolPeriod = async (): Promise<void> => {
    if (!editName.trim()) {
      showError('Period name is required.');
      return;
    }
    if (!editDisplayOrder.trim()) {
      showError('Display order is required.');
      return;
    }
    if (!schoolId || editingSchoolPeriod == null) return;

    setLoading('updateSchoolPeriod', true);
    try {
      await updateSchoolPeriod(schoolId, editingSchoolPeriod, {
        name: editName.trim(),
        displayOrder: Number(editDisplayOrder),
      });

      showSuccess('Period updated.');
      cancelEditSchoolPeriod();
      await fetchSchoolPeriods();
    } catch (error) {
      logError(error, 'updateSchoolPeriod');
      if ((error as ApiErrorLike).status === 409) {
        showError(`A period named "${editName.trim()}" already exists.`);
      } else {
        showError(
          getContextualError(
            error,
            undefined,
            'Failed to update period. Please try again.'
          )
        );
      }
    } finally {
      setLoading('updateSchoolPeriod', false);
    }
  };

  const removeSchoolPeriod = async (
    schoolPeriod: SchoolPeriodResponse
  ): Promise<void> => {
    if (!window.confirm(`Remove ${schoolPeriod.name} from periods list?`)) {
      return;
    }
    if (!schoolId) return;

    setLoading('removeSchoolPeriod', true);
    try {
      await deleteSchoolPeriod(schoolId, schoolPeriod.id);
      showSuccess(`Removed ${schoolPeriod.name} from periods list`);
      await fetchSchoolPeriods();
    } catch (error) {
      logError(error, 'removeSchoolPeriod');
      if ((error as ApiErrorLike).status === 409) {
        showError(
          'This period is used by a schedule type or class section and cannot be removed.'
        );
      } else {
        showError(
          getContextualError(
            error,
            undefined,
            'Failed to remove period. Please try again.'
          )
        );
      }
    } finally {
      setLoading('removeSchoolPeriod', false);
    }
  };

  const content = (
    <>
      <div className="school-period-management">
        <div className="add-school-period-form">
          <h3>Add Period</h3>
          <div className="form-row">
            <input
              type="text"
              placeholder="Period name (e.g., Period 1, Lunch)"
              aria-label="Period name"
              value={newName}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setNewName(e.target.value)
              }
              className={adminStyles['teacher-input']}
              disabled={loadingStates.addSchoolPeriod}
            />
          </div>

          <div className="form-row">
            <input
              type="number"
              min="0"
              placeholder="Display order (e.g., 1)"
              aria-label="Display order"
              value={newDisplayOrder}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setNewDisplayOrder(e.target.value)
              }
              className={adminStyles['teacher-input']}
              disabled={loadingStates.addSchoolPeriod}
            />
          </div>

          <div className={adminStyles['button-group']}>
            <button
              onClick={addSchoolPeriod}
              disabled={loadingStates.addSchoolPeriod}
              className={adminStyles['add-btn']}
            >
              {loadingStates.addSchoolPeriod ? 'Adding...' : 'Add Period'}
            </button>
          </div>
        </div>
      </div>

      <div className="school-periods-list">
        <h3>Current Periods ({schoolPeriods.length})</h3>
        {loadingStates.loadSchoolPeriods ? (
          <div className="loading">Loading periods...</div>
        ) : schoolPeriods.length === 0 ? (
          <p>No periods yet. Add some above.</p>
        ) : (
          <div className={adminStyles['teachers-grid']}>
            {schoolPeriods.map((schoolPeriod) => (
              <div key={schoolPeriod.id} className={adminStyles['teacher-card']}>
                {editingSchoolPeriod === schoolPeriod.id ? (
                  // Edit form
                  <div className="edit-destination-form">
                    <div className="form-group">
                      <label htmlFor={`edit-period-name-${schoolPeriod.id}`}>
                        Period Name:
                      </label>
                      <input
                        id={`edit-period-name-${schoolPeriod.id}`}
                        type="text"
                        value={editName}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                          setEditName(e.target.value)
                        }
                        placeholder="Period name"
                        disabled={loadingStates.updateSchoolPeriod}
                      />
                    </div>

                    <div className="form-group">
                      <label
                        htmlFor={`edit-period-display-order-${schoolPeriod.id}`}
                      >
                        Display Order:
                      </label>
                      <input
                        id={`edit-period-display-order-${schoolPeriod.id}`}
                        type="number"
                        min="0"
                        value={editDisplayOrder}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                          setEditDisplayOrder(e.target.value)
                        }
                        disabled={loadingStates.updateSchoolPeriod}
                      />
                    </div>

                    <div className={adminStyles['button-group']}>
                      <button
                        onClick={submitUpdateSchoolPeriod}
                        disabled={loadingStates.updateSchoolPeriod}
                        className={adminStyles['add-btn']}
                      >
                        {loadingStates.updateSchoolPeriod
                          ? 'Updating...'
                          : 'Update'}
                      </button>
                      <button
                        onClick={cancelEditSchoolPeriod}
                        disabled={loadingStates.updateSchoolPeriod}
                        className={adminStyles['remove-btn']}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  // Display card
                  <>
                    <div className={adminStyles['teacher-info']}>
                      <strong>{schoolPeriod.name}</strong>
                      <div
                        className="text-muted-sm"
                        style={{ marginTop: '4px' }}
                      >
                        Display order: {schoolPeriod.displayOrder}
                      </div>
                    </div>
                    <div className={adminStyles['button-group']}>
                      <button
                        onClick={() => startEditSchoolPeriod(schoolPeriod)}
                        disabled={loadingStates.removeSchoolPeriod}
                        className={adminStyles['btn-edit']}
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => removeSchoolPeriod(schoolPeriod)}
                        disabled={loadingStates.removeSchoolPeriod}
                        className={adminStyles['remove-btn']}
                      >
                        {loadingStates.removeSchoolPeriod
                          ? 'Removing...'
                          : 'Remove'}
                      </button>
                    </div>
                  </>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  );

  if (embedded) {
    return content;
  }

  return (
    <CollapsibleSection
      title="Period Management"
      icon="🕘"
      id="school-period-management"
      defaultExpanded={false}
    >
      {content}
    </CollapsibleSection>
  );
};

export default SchoolPeriodsPanel;
